/* eslint-disable max-len */
/* eslint-disable guard-for-in */
/* eslint-disable no-restricted-syntax */
import _ from 'underscore';

import {
  dropdownTextForCreateNewRadioButton,
  dropdownTextForDraftRadioButton,
  draftStoreName,
  mmcSuggestionTextForNewStores,
  Scenarios,
} from './constants';

// This file contains various utilities to help provide the functionality of the multiple scenarios
// of the gmc-to-mmc-mapping-grid as defined by the Figma.
// See https://www.figma.com/file/fVI4AIU8m7yIvDGILaBWxl/Microsoft-Advertising-Reference-designs?node-id=54932%3A44631
// under the page "MMC import high-fidelity design"

export const defaultValueForDisplayOnTheUI = '-';
export const PLACEHOLDER_RADIO_BUTTON_KEY = '1';
export const ASSOCIATE_RADIO_BUTTON_KEY = '2';
export const CREATE_NEW_RADIO_BUTTON_KEY = '3';

export const isSelectedMappingANewDraftStore =
  ({ row, i18n }) => row.SelectedMapping.Text === dropdownTextForDraftRadioButton(i18n);

export const isSelectedMappingANewStoreToBeCreated = /* istanbul ignore next */
  ({ row, i18n }) => row.SelectedMapping.Text === dropdownTextForCreateNewRadioButton(i18n) ||
    row.SelectedMapping.Text === mmcSuggestionTextForNewStores;

export const isSelectedMappingARealStore =
  row => row.SelectedMapping.MMCId !== null &&
         row.SelectedMapping.MMCId !== defaultValueForDisplayOnTheUI;

const isCreatedDraftStore = ({ MMCStore }) =>
  !_.isUndefined(MMCStore) && MMCStore.IsPlaceholder === true;

export const getCreatedDraftStoreIdForGMCStore = /* istanbul ignore next */
  ({ row }) => !_.isUndefined(row.SuggestedMMCStore.MatchingPlaceholderId) &&
    !_.isNull(row.SuggestedMMCStore.MatchingPlaceholderId) &&
    row.SuggestedMMCStore.MatchingPlaceholderId;

export const doesGMCStoreHaveACreatedDraftStore = /* istanbul ignore next */
  ({ row }) => !_.isUndefined(row.SuggestedMMCStore.MatchingPlaceholderId) &&
    !_.isNull(row.SuggestedMMCStore.MatchingPlaceholderId) &&
    !_.isEmpty(row.SuggestedMMCStore) &&
    row.SuggestedMMCStore.MatchingPlaceholderId !== -1;

export const isSelectedMappingACreatedDraftStore = /* istanbul ignore next */
({ row, MMCStores }) => row.SelectedMapping.MMCId !== null &&
  isCreatedDraftStore({ MMCStore: MMCStores[row.SelectedMapping.MMCId] });

export const checkMappingGridForDraft = /* istanbul ignore next */ ({ mappingGridRows, i18n, MMCStores }) => {
  for (const key in mappingGridRows) {
    const row = mappingGridRows[key];
    if (isSelectedMappingANewDraftStore({ row, i18n }) ||
        isSelectedMappingACreatedDraftStore({ row, MMCStores })) {
      return true;
    }
  }

  return false;
};

const areAllMMCStoresExistingDraftStores = /* istanbul ignore next */ ({ MMCStores }) => {
  for (const key in MMCStores) {
    const MMCStore = MMCStores[key];
    if (!isCreatedDraftStore({ MMCStore })) {
      return false;
    }
  }

  return true;
};

export const determineScenario = ({ GMCStores, MMCStores, logger }) => {
  const numGMCStores = Object.keys(GMCStores).length;
  const numMMCStores = Object.keys(MMCStores).length;

  if (numGMCStores === 0) {
    logger.logError('BSC Import - DetermineScenario - the number of Google Merchant Center stores is unexpectedly zero');
    return Scenarios.NOT_DETERMINED;
  }

  /* istanbul ignore next */
  if (numMMCStores === 0 || areAllMMCStoresExistingDraftStores({ MMCStores })) {
    return Scenarios.NO_MMC_STORES;
  } else if (numMMCStores === 1 && numGMCStores === 1) {
    return Scenarios.SINGLE_MMC_STORE_SINGLE_GMC_STORE;
  } else if (numMMCStores === 1 && numGMCStores > 1) {
    return Scenarios.SINGLE_MMC_STORE_MULTIPLE_GMC_STORES;
  } else if (numMMCStores > 1 && numGMCStores === 1) {
    return Scenarios.MULTIPLE_MMC_STORE_SINGLE_GMC_STORE;
  } else if (numMMCStores > 1 && numGMCStores > 1) {
    return Scenarios.MULTIPLE_MMC_STORE_MULTIPLE_GMC_STORES;
  }

  /* istanbul ignore next */
  logger.logError('BSC Import - DetermineScenario - the scenario could not be determined given the input Microsoft and Google Merchant Center stores');
  /* istanbul ignore next */
  return Scenarios.NOT_DETERMINED;
};

const isEmptyString = stringValue => _.isString(stringValue) && stringValue === '';

// TODO: consider adding more demo/unit tests to fully cover all methods that have istanbul ignores
export const parseGmcStoreName = GMCStore /* istanbul ignore next */ =>
  (_.isUndefined(GMCStore) || _.isNull(GMCStore) ||
   _.isUndefined(GMCStore.StoreName) || _.isNull(GMCStore.StoreName) ||
   isEmptyString(GMCStore.StoreName) ?
    defaultValueForDisplayOnTheUI : GMCStore.StoreName);

export const parseGmcDomain = GMCStore /* istanbul ignore next */ =>
  (_.isUndefined(GMCStore) || _.isNull(GMCStore) ||
   _.isUndefined(GMCStore.Domain) || _.isNull(GMCStore.Domain) ||
   isEmptyString(GMCStore.Domain) ?
    defaultValueForDisplayOnTheUI : GMCStore.Domain);

export const parseHasAccessToGMCStore = GMCStore /* istanbul ignore next */ =>
  (_.isUndefined(GMCStore) || _.isNull(GMCStore) ||
   _.isUndefined(GMCStore.HasAccess) || _.isNull(GMCStore.HasAccess) ? false : GMCStore.HasAccess);

export const parseOtherMMCStoreOptions = SuggestedMMCStore /* istanbul ignore next */ =>
  (_.isUndefined(SuggestedMMCStore) ||
 _.isNull(SuggestedMMCStore) ||
 _.isUndefined(SuggestedMMCStore.OtherMMCStoreOptions) ||
 _.isNull(SuggestedMMCStore.OtherMMCStoreOptions) ?
    // TODO: this may cause issues
    [] :
    SuggestedMMCStore.OtherMMCStoreOptions);

export const generateCreateNewStoreRow = ({
  GMCMerchantId, GMCStore, SuggestedMMCStore, i18n,
}) => ({
  GMCMerchantId,
  GMCStoreName: parseGmcStoreName(GMCStore),
  GMCStoreDomain: parseGmcDomain(GMCStore),
  // GMCStore.HasAccess will always be true for a Create New Store suggestion
  HasAccess: true,
  SelectedMapping: {
    // Localizing since we'll display this in the UI
    Text: dropdownTextForCreateNewRadioButton(i18n),
    MMCId: null,
  },
  SuggestedMMCStore,
  OtherMMCStoreOptions: [],
});

// If changing the name of the GMCMerchantId key, change the setKey on the DetailsList
export const generateRealStoreRow = ({
  GMCMerchantId, GMCStore, MMCStore, MMCStoreId, SuggestedMMCStore,
}) => ({
  GMCMerchantId,
  GMCStoreName: parseGmcStoreName(GMCStore),
  GMCStoreDomain: parseGmcDomain(GMCStore),
  HasAccess: parseHasAccessToGMCStore(GMCStore),
  SelectedMapping: {
    Text: MMCStore.StoreName,
    MMCId: MMCStoreId,
  },
  // Could refactor the below 2 fields into an object called RowProperties
  SuggestedMMCStore,
  // Could rename this variable here and on MMC side since it's
  // only used in the multiple domain match case.
  OtherMMCStoreOptions: parseOtherMMCStoreOptions(SuggestedMMCStore),
  // Consider an enum for GMCStore.HasAccess: NOT_SIGNED_IN, HAS_ACCESS, NO_ACCESS
});

export const generateDraftStoreRow = ({
  i18n, GMCMerchantId, GMCStore,
}) => ({
  GMCMerchantId,
  GMCStoreName: parseGmcStoreName(GMCStore),
  GMCStoreDomain: parseGmcDomain(GMCStore),
  HasAccess: parseHasAccessToGMCStore(GMCStore),
  SelectedMapping: {
    Text: dropdownTextForDraftRadioButton(i18n),
    MMCId: null,
  },
  SuggestedMMCStore: {},
  OtherMMCStoreOptions: [],
});

export const generateMmcGridRow = (MMCStoreId, MMCStore) => {
  // Don't show created draft (placeholder) stores in the MMC Grid
  if (!isCreatedDraftStore({ MMCStore })) {
    return {
      MMCStoreName: MMCStore.StoreName,
      MMCStoreId,
      MMCStoreDomain: MMCStore.Domain,
    };
  }

  return {};
};

// Use this function with array.sort(func)
const comparatorForSortingByInteger = (item1, item2) => item1 - item2;

// If changing the name of the MMCStoreId key, change the setKey on the DetailsList
export const generateMMCGridRows = (MMCStores, logger) => {
  let mmcGridRows = [];

  for (const key in MMCStores) {
    const mmcStore = MMCStores[key];
    const mmcStoreId = key;
    /* istanbul ignore next */
    if (_.isUndefined(mmcStore.StoreName) ||
        _.isUndefined(mmcStore.Domain) ||
        // Recall that placeholder stores are the same as draft stores
        _.isUndefined(mmcStore.IsPlaceholder)) {
      // IsPlaceholder is not used in this method, but since we do MMCStores validation here,
      // putting that validation here too. IsPlaceholder is used for created placeholder stores.
      logger.logError('BSC Import - GenerateMMCGridRows - MMC store is missing StoreName or Domain or IsPlaceholder');
      return [];
    }

    const gridRow = generateMmcGridRow(mmcStoreId, mmcStore);

    if (!_.isEmpty(gridRow)) {
      mmcGridRows.push(gridRow);
    }
  }

  const formatItem = item => parseInt(item.MMCStoreId, 10);

  mmcGridRows = mmcGridRows.sort((item1, item2) =>
    comparatorForSortingByInteger(formatItem(item1), formatItem(item2)));

  return mmcGridRows;
};

export const generateInfoForMMCGrid = (MMCStores, logger) => {
  const mmcGridRows = generateMMCGridRows(MMCStores, logger);

  return {
    mmcGridRows,
    MMCStores,
  };
};

export const hasValidStoreId = SuggestedMMCStore =>
  !_.isUndefined(SuggestedMMCStore.StoreId) &&
  !_.isNull(SuggestedMMCStore.StoreId) &&
  SuggestedMMCStore.StoreId > 0;

export const isRealStoreWithoutPerfectMatch = SuggestedMMCStore => !_.isEmpty(SuggestedMMCStore) &&
  hasValidStoreId(SuggestedMMCStore) &&
  !_.isUndefined(SuggestedMMCStore.IsDomainValidated) &&
  !_.isNull(SuggestedMMCStore.IsDomainValidated) &&
  SuggestedMMCStore.IsDomainValidated === false;

export const areThereMultipleDomainMatchingMMCStores =
  SuggestedMMCStore =>
    hasValidStoreId(SuggestedMMCStore) &&
    !_.isUndefined(SuggestedMMCStore.IsDomainValidated) &&
    !_.isNull(SuggestedMMCStore.IsDomainValidated) &&
    SuggestedMMCStore.IsDomainValidated === true &&
    !_.isUndefined(SuggestedMMCStore.OtherMMCStoreOptions) &&
    !_.isNull(SuggestedMMCStore.OtherMMCStoreOptions) &&
    SuggestedMMCStore.OtherMMCStoreOptions.length > 0;

export const isPerfectMatch = ({ SuggestedMMCStore }) => {
  const { OtherMMCStoreOptions } = SuggestedMMCStore;

  return hasValidStoreId(SuggestedMMCStore) &&
          !_.isUndefined(SuggestedMMCStore.IsDomainValidated) &&
          !_.isNull(SuggestedMMCStore.IsDomainValidated) &&
          SuggestedMMCStore.IsDomainValidated === true &&
          (_.isUndefined(OtherMMCStoreOptions) || _.isNull(OtherMMCStoreOptions) || OtherMMCStoreOptions.length === 0);
};

// Could refactor every method that takes SuggestedMMCStore and OtherMMCStoreOptions to
// only take SuggestedMMCStore.
// Note that this is called by mapping-editor.js who will check
// that areThereMultipleDomainMatchingMMCStores(...) is true prior to calling this method
// Tested via demo tests and SI E2E tests
export const generateMMCGridRowsForMultipleDomainMatchScenario = /* istanbul ignore next */
  (SuggestedMMCStore, OtherMMCStoreOptions, MMCStores, logger) => {
    const mmcStoresForMultipleDomainMatchScenario = {};
    let storeId = SuggestedMMCStore.StoreId;
    mmcStoresForMultipleDomainMatchScenario[storeId] = MMCStores[storeId];

    for (const i in OtherMMCStoreOptions) {
      const otherMMCStoreOption = OtherMMCStoreOptions[i];
      if (!isPerfectMatch({ SuggestedMMCStore: otherMMCStoreOption })) {
        logger.logError('BSC Import - GenerateMMCGridRowsForMultipleDomainMatchScenario - otherMMCStoreOption is a suggested MMC store in an unexpected format');
        return [];
      }

      storeId = otherMMCStoreOption.StoreId;
      mmcStoresForMultipleDomainMatchScenario[storeId] = MMCStores[storeId];
    }

    const MMCGridRowsForMultipleDomainMatchScenario =
      generateMMCGridRows(mmcStoresForMultipleDomainMatchScenario, logger);

    return MMCGridRowsForMultipleDomainMatchScenario;
  };

export const isDraftStore = SuggestedMMCStore => _.isEmpty(SuggestedMMCStore);

export const isCreateNewStore = (SuggestedMMCStore, i18n, isSignedIn) => isSignedIn === true &&
 !_.isEmpty(SuggestedMMCStore) &&
 !_.isUndefined(SuggestedMMCStore.StoreName) &&
 !_.isNull(SuggestedMMCStore.StoreName) &&
 // SuggestedMMCStore.StoreName for "Create a new store" from MMC's GetMappings response is not localized.
 // However, from setDropdown, this value is localized via initDropdownTextPerRadioButton, so check both.
 (SuggestedMMCStore.StoreName === dropdownTextForCreateNewRadioButton(i18n) ||
  SuggestedMMCStore.StoreName === mmcSuggestionTextForNewStores);

// Tested via demo tests and SI E2E tests
export const isCellReadOnly = /* istanbul ignore next */ ({
  isSignedIn, mappingGridRow, scenario, MMCStores,
}) => {
  const { SuggestedMMCStore } = mappingGridRow;

  const isReadOnlyDraftStore =
    (isDraftStore(SuggestedMMCStore) || isSelectedMappingACreatedDraftStore({ row: mappingGridRow, MMCStores })) &&
      scenario === Scenarios.NO_MMC_STORES &&
      !isSignedIn;

  const isStoreAPerfectMatch = isPerfectMatch({ SuggestedMMCStore });

  return isReadOnlyDraftStore || isStoreAPerfectMatch;
};

// TODO: remove and update mocks after MMC implements this logic
export const isSingleMmcStoreScenarioAndNotSignedIn = (scenario, isSignedIn) => (
  (
    scenario === Scenarios.SINGLE_MMC_STORE_SINGLE_GMC_STORE ||
    scenario === Scenarios.SINGLE_MMC_STORE_MULTIPLE_GMC_STORES
  ) &&
  isSignedIn === false
);

// TODO: remove and update mocks after MMC implements this logic
export const isSingleMmcStoreScenarioAndSignedInWithNoAccess = (scenario, isSignedIn, GMCStore) => (
  (
    scenario === Scenarios.SINGLE_MMC_STORE_SINGLE_GMC_STORE ||
    scenario === Scenarios.SINGLE_MMC_STORE_MULTIPLE_GMC_STORES
  ) &&
  isSignedIn === true &&
  !_.isUndefined(GMCStore) &&
  !_.isNull(GMCStore) &&
  GMCStore.HasAccess === false
);

// Tested via demo and SI E2E tests
export const scenarioHasMmcStores = /* istanbul ignore next */ scenario =>
  scenario === Scenarios.SINGLE_MMC_STORE_SINGLE_GMC_STORE ||
  scenario === Scenarios.SINGLE_MMC_STORE_MULTIPLE_GMC_STORES ||
  scenario === Scenarios.MULTIPLE_MMC_STORE_SINGLE_GMC_STORE ||
  scenario === Scenarios.MULTIPLE_MMC_STORE_MULTIPLE_GMC_STORES;

export const getReadOnlyCellTooltipText = /* istanbul ignore next */ ({
  i18n, mappingGridRow, logger, MMCStores,
}) => {
  const { SuggestedMMCStore } = mappingGridRow;

  /* istanbul ignore next */
  if (isDraftStore(SuggestedMMCStore) || isSelectedMappingACreatedDraftStore({ row: mappingGridRow, MMCStores })) {
    return i18n.getString(_TL_('Draft store'));
  } else if (isPerfectMatch({ SuggestedMMCStore })) {
    return i18n.getString(_TL_('Store matched'));
  }

  /* istanbul ignore next */
  logger.logError('BSC Import - GetReadOnlyCellTooltipText - SuggestedMMCStore entered the read only cell path but is not supported');
  /* istanbul ignore next */
  return '';
};

export const generateMappingGridRowFromGetMappingsResponse = ({
  // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
  GMCMerchantId, i18n, isSignedIn, logger, response, scenario,
}) => {
  let row = null;
  let rowHasDraftStore = false;

  const {
    GoogleMerchantIdToStoreMap, MMCStores, GMCStores,
  } = response;

  const GMCStore = GMCStores[GMCMerchantId];

  const mappingGridRowFromMMC = GoogleMerchantIdToStoreMap[GMCMerchantId];
  const { SuggestedMMCStore } = mappingGridRowFromMMC;

  // TODO: remove and update mocks after MMC implements this logic
  /* istanbul ignore else */
  if (isSingleMmcStoreScenarioAndNotSignedIn(scenario, isSignedIn) ||
      isSingleMmcStoreScenarioAndSignedInWithNoAccess(scenario, isSignedIn, GMCStore)) {
    const MMCStoreId = Object.keys(MMCStores)[0];
    const MMCStore = Object.values(MMCStores)[0];
    row = generateRealStoreRow({
      GMCMerchantId, GMCStore, MMCStore, MMCStoreId, SuggestedMMCStore: MMCStore,
    });
  } else if (isDraftStore(SuggestedMMCStore)) {
    rowHasDraftStore = true;
    row = generateDraftStoreRow({
      i18n, GMCMerchantId, GMCStore,
    });
  } else if (isRealStoreWithoutPerfectMatch(SuggestedMMCStore) ||
              isPerfectMatch({ SuggestedMMCStore }) ||
              areThereMultipleDomainMatchingMMCStores(SuggestedMMCStore)
  ) {
    const MMCStoreId = SuggestedMMCStore.StoreId;
    const MMCStore = MMCStores[MMCStoreId];

    if (isCreatedDraftStore({ MMCStore })) {
      rowHasDraftStore = true;
      const draftName = draftStoreName({ draftStoreId: MMCStoreId, i18n });
      SuggestedMMCStore.StoreName = draftName;
      MMCStore.StoreName = draftName;
    }

    row = generateRealStoreRow({
      GMCMerchantId, GMCStore, MMCStore, MMCStoreId, SuggestedMMCStore,
    });
  } else if (isCreateNewStore(SuggestedMMCStore, i18n, isSignedIn)) {
    row = generateCreateNewStoreRow({
      i18n, GMCMerchantId, GMCStore, SuggestedMMCStore,
    });
  } else {
    logger.logError('BSC Import - GenerateMappingGridRowsFromGetMappingsResponse - the suggested store does not fall into one of the supported categories.' +
      ` Suggested store: ${SuggestedMMCStore}. Defaulting to draft store`);

    // Default to draft store if none of the above scenarios were met
    rowHasDraftStore = true;
    row = generateDraftStoreRow({
      i18n, GMCMerchantId, GMCStore,
    });
  }

  return { row, rowHasDraftStore };
};

export const generateMappingGridRowsFromGetMappingsResponse = ({
  response, isSignedIn, i18n, logger,
}) => {
  let mappingGridRows = [];
  let hasDraftStore = false;

  const errorResponse = {
    isError: true,
  };

  /* istanbul ignore next */
  if (_.isUndefined(response.GoogleMerchantIdToStoreMap) ||
      _.isUndefined(response.MMCStores) ||
      _.isUndefined(response.GMCStores) ||
      _.isUndefined(response.GMCEmail)) {
    logger.logError('BSC Import - GenerateMappingGridRowsFromGetMappingsResponse - MMC API MatchingStoresByGoogleMerchantIdsV2 (GetMappings) response is in an unexpected format');
    return errorResponse;
  }

  const {
    GoogleMerchantIdToStoreMap, MMCStores, GMCStores, GMCEmail: gmcEmailAddress,
  } = response;

  const infoForMMCGrid = generateInfoForMMCGrid(MMCStores, logger);

  const scenario = determineScenario({ GMCStores, MMCStores, logger });

  for (const key in GoogleMerchantIdToStoreMap) {
    /* istanbul ignore else */
    if (!_.isUndefined(GoogleMerchantIdToStoreMap[key].SuggestedMMCStore)) {
      const GMCMerchantId = key;

      const rowData = generateMappingGridRowFromGetMappingsResponse({
        GMCMerchantId, i18n, isSignedIn, logger, response, scenario,
      });

      const { row, rowHasDraftStore } = rowData;

      mappingGridRows.push(row);
      hasDraftStore = rowHasDraftStore ? true : hasDraftStore;
    } else {
      logger.logError('BSC Import - GenerateMappingGridRowsFromGetMappingsResponse - the SuggestedMMCStore field is missing from the MatchingStoresByGoogleMerchantIdsV2 (GetMappings) response');
      return errorResponse;
    }
  }

  const formatItem = item => parseInt(item.GMCMerchantId, 10);

  mappingGridRows = mappingGridRows.sort((item1, item2) =>
    comparatorForSortingByInteger(formatItem(item1), formatItem(item2)));

  return {
    gmcEmailAddress,
    hasDraftStore,
    mappingGridRows,
    infoForMMCGrid,
    scenario,
    isError: false,
  };
};

export const determineRadioButtonWhenConstructed = ({
  i18n,
  logger,
  row,
  infoForMMCGrid,
}) => {
  const { MMCStores } = infoForMMCGrid;

  /* istanbul ignore else */
  if (isSelectedMappingANewDraftStore({ row, i18n }) ||
      isSelectedMappingACreatedDraftStore({ row, MMCStores })) {
    return PLACEHOLDER_RADIO_BUTTON_KEY;
  } else if (isSelectedMappingARealStore(row)) {
    return ASSOCIATE_RADIO_BUTTON_KEY;
  } else if (isSelectedMappingANewStoreToBeCreated({ row, i18n })) {
    return CREATE_NEW_RADIO_BUTTON_KEY;
  }

  logger.logError('BSC Import - determineRadioButtonWhenConstructed - selected mapping is in an unexpected format. Defaulting to first radio button');
  return this.choiceGroupOptions[0].key;
};

export const determineSelectedMMCStore = ({ selectedRadioButtonKey, selectedRow, MMCStores }) => {
  let selectedMMCStore = null;
  if (selectedRadioButtonKey === ASSOCIATE_RADIO_BUTTON_KEY &&
      isSelectedMappingARealStore(selectedRow)) {
    const { SelectedMapping } = selectedRow;
    let storeId = SelectedMapping.MMCId;
    storeId = _.isNumber(storeId) ? storeId.toString() : storeId;
    selectedMMCStore = {
      MMCStoreName: MMCStores[storeId].StoreName,
      MMCStoreDomain: MMCStores[storeId].Domain,
      MMCStoreId: storeId,
    };
  }

  return selectedMMCStore;
};
